import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingUp, FiClock, FiCheckCircle, FiImage, FiExternalLink, FiX, FiZap, FiTarget, FiBell, FiInfo, FiAlertTriangle, FiCheck } = FiIcons;

const Dashboard = () => {
  const { user, updateLocalUser } = useAuth();
  const [stats, setStats] = useState({ total: 0, pending: 0, paid: 0 });
  const [recentPaid, setRecentPaid] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    if (user) {
      loadStats();
      loadPaidHistory();
      fetchCharacters();
    }
  }, [user]);

  const fetchCharacters = async () => {
    const { data } = await supabase.from(TABLES.CHARACTERS).select('*').eq('pilot_id', user.id);
    setCharacters(data || []);
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data: logs } = await supabase.from(TABLES.LOGS).select('value_at_log, status').eq('pilot_id', user.id);
      const total = logs?.reduce((acc, curr) => acc + (Number(curr.value_at_log) || 0), 0) || 0;
      const pending = logs?.filter(l => l.status === 'unpaid').reduce((acc, curr) => acc + (Number(curr.value_at_log) || 0), 0) || 0;
      const paid = logs?.filter(l => l.status === 'paid').reduce((acc, curr) => acc + (Number(curr.value_at_log) || 0), 0) || 0;
      setStats({ total, pending, paid });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadPaidHistory = async () => {
    const { data } = await supabase
      .from(TABLES.LOGS)
      .select(`*, item:${TABLES.ITEMS}(name)`)
      .eq('pilot_id', user.id)
      .eq('status', 'paid')
      .order('created_at', { ascending: false })
      .limit(10);
    setRecentPaid(data || []);
  };

  const handleSalaryAction = async (charId, action) => {
    setProcessingId(charId);
    try {
      const char = characters.find(c => c.id === charId);
      let payload = {};
      
      if (action === 'accept') {
        payload = {
          base_salary: char.pending_base_salary,
          pending_base_salary: null,
          salary_status: 'approved',
          pilot_response: ''
        };
      } else {
        const reason = prompt("Why are you contesting this salary change?");
        if (!reason) return;
        payload = {
          salary_status: 'contested',
          pilot_response: reason
        };
      }

      const { error } = await supabase.from(TABLES.CHARACTERS).update(payload).eq('id', charId);
      if (error) throw error;
      
      await fetchCharacters();
      alert(`Salary update ${action}ed!`);
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const acknowledgeUpdates = async () => {
    const now = new Date().toISOString();
    const { error } = await supabase
      .from(TABLES.USERS)
      .update({ last_viewed_profile_at: now })
      .eq('id', user.id);
    if (!error) {
      updateLocalUser({ ...user, last_viewed_profile_at: now });
    }
  };

  const totalBaseSalary = characters.reduce((sum, char) => sum + (Number(char.base_salary) || 0), 0);
  const pendingCharacterUpdates = characters.filter(c => c.salary_status === 'pending_pilot');
  const hasProfileUpdate = user.profile_updated_at && (!user.last_viewed_profile_at || new Date(user.profile_updated_at) > new Date(user.last_viewed_profile_at));

  if (loading) return <div className="animate-pulse flex items-center justify-center h-64">Loading Pilot Data...</div>;

  return (
    <div className="space-y-12 animate-in fade-in duration-500">
      <header>
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">Pilot Statistics</h2>
        <p className="text-gray-500 font-medium">Earning overview and roster tier.</p>
      </header>

      {/* Pending Salary Approvals */}
      <AnimatePresence>
        {pendingCharacterUpdates.length > 0 && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6">
            <h3 className="text-[10px] font-black text-amber-600 uppercase tracking-widest ml-4 flex items-center gap-2">
              <FiAlertTriangle /> Contract Updates Requiring Action
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pendingCharacterUpdates.map(char => (
                <div key={char.id} className="bento-card border-l-4 border-l-amber-500 !p-6 bg-amber-50/20">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-black text-gray-900">{char.name}</h4>
                      <p className="text-[10px] font-black text-amber-600 uppercase">Proposed Salary Change</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-400 line-through">₱{char.base_salary}</p>
                      <p className="text-xl font-black text-emerald-600">₱{char.pending_base_salary}</p>
                    </div>
                  </div>
                  {char.master_remarks && (
                    <p className="text-xs text-gray-500 italic mb-6 bg-white/50 p-3 rounded-xl border border-amber-100">
                      Master: "{char.master_remarks}"
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleSalaryAction(char.id, 'accept')} 
                      disabled={processingId === char.id}
                      className="flex-1 py-3 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase flex items-center justify-center gap-2"
                    >
                      <FiCheck /> Accept Change
                    </button>
                    <button 
                      onClick={() => handleSalaryAction(char.id, 'contest')} 
                      disabled={processingId === char.id}
                      className="flex-1 py-3 bg-amber-500 text-white rounded-xl text-[10px] font-black uppercase flex items-center justify-center gap-2"
                    >
                      <FiX /> Contest
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {hasProfileUpdate && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="p-8 bg-blue-600 text-white rounded-[2.5rem] shadow-2xl shadow-blue-200 border border-blue-400 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-[1.5rem] flex items-center justify-center backdrop-blur-md">
                <SafeIcon icon={FiBell} className="text-3xl animate-bounce" />
              </div>
              <div>
                <h4 className="text-xl font-black">Profile Updates Detected</h4>
                <p className="text-blue-100 font-bold text-sm mt-1">Your Master has updated your earning model or character roster.</p>
              </div>
            </div>
            <button onClick={acknowledgeUpdates} className="px-8 py-4 bg-white text-blue-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-50 transition-all shadow-lg"> Got it, thanks! </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {user.is_base_pay_only ? (
          <div className="bg-white p-6 md:p-8 rounded-[2rem] border border-gray-100 shadow-sm border-l-4 border-l-amber-500">
            <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mb-4"><SafeIcon icon={FiZap} className="text-2xl" /></div>
            <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Monthly Base Pay</h3>
            <p className="text-3xl md:text-4xl font-black text-gray-900">₱{totalBaseSalary.toLocaleString()}</p>
            <p className="text-[9px] text-gray-400 font-bold uppercase mt-2 tracking-widest flex items-center gap-1"> <FiInfo /> Sum of character tiers </p>
          </div>
        ) : (
          <div className="bg-white p-6 md:p-8 rounded-[2rem] border border-gray-100 shadow-sm border-l-4 border-l-blue-500">
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-4"><SafeIcon icon={FiTarget} className="text-2xl" /></div>
            <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Monthly Earnings Cap</h3>
            <p className="text-3xl md:text-4xl font-black text-gray-900">₱{user.monthly_cap?.toLocaleString()}</p>
          </div>
        )}
        <div className="bg-white p-6 md:p-8 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-4"><SafeIcon icon={FiTrendingUp} className="text-2xl" /></div>
          <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Total Lifetime</h3>
          <p className="text-3xl md:text-4xl font-black text-gray-900">₱{stats.total.toLocaleString()}</p>
        </div>
        <div className="bg-white p-6 md:p-8 rounded-[2rem] border border-gray-100 shadow-sm border-l-4 border-l-emerald-500">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-4"><SafeIcon icon={FiCheckCircle} className="text-2xl" /></div>
          <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Settled</h3>
          <p className="text-3xl md:text-4xl font-black text-emerald-600">₱{stats.paid.toLocaleString()}</p>
        </div>
      </div>

      <div className="bento-card">
        <h3 className="text-xl font-black text-gray-900 mb-8">Recent History</h3>
        <div className="space-y-4">
          {recentPaid.map(log => (
            <div key={log.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-6 bg-gray-50 rounded-3xl border border-gray-100 gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-emerald-600 shadow-sm">
                  <SafeIcon icon={FiCheckCircle} className="text-xl" />
                </div>
                <div>
                  <div className="font-black text-gray-900">{log.item?.name}</div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Settled on {new Date(log.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;